//----------------------------------------------------------------------------------------------------------------------
// Save Manager
//----------------------------------------------------------------------------------------------------------------------

import type { Disposable } from '../interfaces/lifecycle.ts';
import type { LoggerInterface } from '../interfaces/logger.ts';
import type { GameEntityManager } from './entity.ts';
import type { LevelManager } from './level.ts';

// Utils
import { type LoggingUtility, SAGELogger } from '../utils/logger.ts';

//----------------------------------------------------------------------------------------------------------------------
// Types
//----------------------------------------------------------------------------------------------------------------------

export interface SaveData
{
    version : number;
    levelName : string;
    entities : SerializedEntity[];
    custom : Record<string, unknown>;
}

export interface SerializedEntity
{
    id : string;
    type : string;
    name ?: string;
    tags : string[];
    state : object;
    parentId ?: string;
    transform ?: {
        position : { x : number; y : number; z : number };
        rotation : { x : number; y : number; z : number };
        scaling : { x : number; y : number; z : number };
    };
}

//----------------------------------------------------------------------------------------------------------------------

type BeforeSerializeHook = () => Record<string, unknown>;
type AfterDeserializeHook = (custom : Record<string, unknown>) => void;

//----------------------------------------------------------------------------------------------------------------------

export class SaveManager implements Disposable
{
    private _entityManager : GameEntityManager;
    private _levelManager : LevelManager;
    private _log : LoggerInterface;

    private _beforeSerializeHooks : BeforeSerializeHook[] = [];
    private _afterDeserializeHooks : AfterDeserializeHook[] = [];

    constructor(
        entityManager : GameEntityManager,
        levelManager : LevelManager,
        logger ?: LoggingUtility
    )
    {
        this._entityManager = entityManager;
        this._levelManager = levelManager;
        this._log = logger?.getLogger('SaveManager') || new SAGELogger('SaveManager');
        this._log.info('SaveManager initialized');
    }

    //------------------------------------------------------------------------------------------------------------------
    // Hook Registration
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Register a hook that returns custom data to include in save data.
     * Multiple hooks can be registered; their results are merged into the `custom` field.
     */
    onBeforeSerialize(hook : BeforeSerializeHook) : void
    {
        this._beforeSerializeHooks.push(hook);
    }

    /**
     * Register a hook to process custom data when loading a save.
     * Multiple hooks can be registered; each receives the full custom data object.
     */
    onAfterDeserialize(hook : AfterDeserializeHook) : void
    {
        this._afterDeserializeHooks.push(hook);
    }

    //------------------------------------------------------------------------------------------------------------------
    // Serialize
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Captures the current game state as a serializable SaveData object.
     * Iterates all entities, captures transforms, state, tags, and parent references.
     * Calls all beforeSerialize hooks to gather custom data.
     */
    serialize() : SaveData
    {
        this._log.debug('Serializing game state...');

        const levelName = this._levelManager.currentLevel?.name ?? '';
        const entities : SerializedEntity[] = [];

        for(const entity of this._entityManager.getAllEntities())
        {
            const serialized : SerializedEntity = {
                id: entity.id,
                type: entity.type,
                tags: Array.from(entity.tags),
                state: structuredClone(entity.state),
            };

            if(entity.name)
            {
                serialized.name = entity.name;
            }

            if(entity.parent)
            {
                serialized.parentId = entity.parent.id;
            }

            if(entity.node)
            {
                const { position, rotation, scaling } = entity.node;
                serialized.transform = {
                    position: { x: position.x, y: position.y, z: position.z },
                    rotation: { x: rotation.x, y: rotation.y, z: rotation.z },
                    scaling: { x: scaling.x, y: scaling.y, z: scaling.z },
                };
            }

            entities.push(serialized);
        }

        // Collect custom data from hooks
        let custom : Record<string, unknown> = {};
        for(const hook of this._beforeSerializeHooks)
        {
            const hookData = hook();
            custom = { ...custom, ...hookData };
        }

        const saveData : SaveData = {
            version: 1,
            levelName,
            entities,
            custom,
        };

        this._log.info(`Serialized ${ entities.length } entities from level "${ levelName }"`);
        return saveData;
    }

    //------------------------------------------------------------------------------------------------------------------
    // Deserialize
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Restores game state from a SaveData object.
     *
     * Flow:
     * 1. Transition to the saved level
     * 2. Destroy all existing entities (level-spawned ones)
     * 3. Recreate entities from save data
     * 4. Restore transforms on entities with nodes
     * 5. Re-establish parent-child hierarchy
     * 6. Call afterDeserialize hooks
     */
    async deserialize(data : SaveData) : Promise<void>
    {
        this._log.debug(`Deserializing save data (version ${ data.version }, level "${ data.levelName }")...`);

        // 1. Transition to the level
        await this._levelManager.transition(data.levelName);

        // 2. Destroy all existing entities spawned by the level
        const existingIds : string[] = [];
        for(const entity of this._entityManager.getAllEntities())
        {
            existingIds.push(entity.id);
        }

        for(const id of existingIds)
        {
            // eslint-disable-next-line no-await-in-loop
            await this._entityManager.destroyEntity(id);
        }

        // 3. Recreate entities — track old ID -> new ID mapping for hierarchy restoration
        const idMap = new Map<string, string>();

        for(const serialized of data.entities)
        {
            // eslint-disable-next-line no-await-in-loop
            const entity = await this._entityManager.createEntity(serialized.type, {
                name: serialized.name,
                initialState: serialized.state,
                tags: serialized.tags,
            });

            idMap.set(serialized.id, entity.id);

            // 4. Restore transform if present
            if(serialized.transform && entity.node)
            {
                const { position, rotation, scaling } = serialized.transform;
                entity.node.position.x = position.x;
                entity.node.position.y = position.y;
                entity.node.position.z = position.z;
                entity.node.rotation.x = rotation.x;
                entity.node.rotation.y = rotation.y;
                entity.node.rotation.z = rotation.z;
                entity.node.scaling.x = scaling.x;
                entity.node.scaling.y = scaling.y;
                entity.node.scaling.z = scaling.z;
            }
        }

        // 5. Restore parent-child hierarchy
        for(const serialized of data.entities)
        {
            if(serialized.parentId)
            {
                const newParentId = idMap.get(serialized.parentId);
                const newChildId = idMap.get(serialized.id);

                if(newParentId && newChildId)
                {
                    this._entityManager.addChild(newParentId, newChildId);
                }
                else
                {
                    this._log.warn(
                        `Could not restore parent-child: ${ serialized.id } -> ${ serialized.parentId } `
                        + `(mapped: ${ newChildId } -> ${ newParentId })`
                    );
                }
            }
        }

        // 6. Call afterDeserialize hooks
        for(const hook of this._afterDeserializeHooks)
        {
            hook(data.custom);
        }

        this._log.info(`Deserialized ${ data.entities.length } entities for level "${ data.levelName }"`);
    }

    //------------------------------------------------------------------------------------------------------------------
    // Lifecycle
    //------------------------------------------------------------------------------------------------------------------

    async $teardown() : Promise<void>
    {
        this._beforeSerializeHooks = [];
        this._afterDeserializeHooks = [];
        this._log.info('SaveManager torn down');
    }
}

//----------------------------------------------------------------------------------------------------------------------
